// ============================================================
//  ALTIMO Medical Center — IndicacionesEnfermeriaPanel.jsx
//  Panel compartido: el médico registra una indicación para
//  enfermería con hora programada (única o recurrente cada N
//  horas). El sistema (main.js → revisarIndicacionesEnfermeria)
//  avisa minutos_aviso antes de la hora vía notificación de
//  campana + notificación nativa del sistema operativo, y marca
//  'Vencida' si pasan 30 min sin que enfermería la cumpla.
//  Reutilizado por: ExpedientePaciente.jsx, Formularios.jsx
// ============================================================

import { useState, useEffect, useCallback } from 'react';
import api from '../apiClient';
import { useConfirmar } from './ModalConfirmar';

const S = {
  card:   { background: '#fff', borderRadius: '10px', border: '1px solid #dde3ea', padding: '18px', marginBottom: '14px' },
  fila2:  { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' },
  fila3:  { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '14px' },
  titulo: {
    fontSize: '12px', fontWeight: '700', color: '#1B2A5E',
    textTransform: 'uppercase', letterSpacing: '.05em',
    marginBottom: '14px', paddingBottom: '10px', borderBottom: '2px solid #e8f0fb',
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  },
  input:  { width: '100%', fontSize: '13px', padding: '8px 10px', border: '1px solid #c0ccda', borderRadius: '7px', background: '#fff', color: '#333', outline: 'none', boxSizing: 'border-box' },
  label:  { display: 'block', fontSize: '11px', fontWeight: '600', color: '#6c757d', marginBottom: '4px', textTransform: 'uppercase', letterSpacing: '.04em' },
  campo:  { marginBottom: '12px' },
  tabla:  { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:     { textAlign: 'left', padding: '8px 12px', fontSize: '11px', color: '#6c757d', fontWeight: '600', borderBottom: '2px solid #e8f0fb', background: '#f8fafd' },
  td:     { padding: '9px 12px', borderBottom: '1px solid #eef1f6', color: '#333' },
  btnPrimario:   { background: '#1B2A5E', color: '#fff', border: 'none', borderRadius: '7px', padding: '8px 18px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' },
  btnVerde:      { background: '#0c8599', color: '#fff', border: 'none', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
  btnSecundario: { background: '#fff', color: '#495057', border: '1px solid #c0ccda', borderRadius: '7px', padding: '7px 14px', fontSize: '12px', cursor: 'pointer' },
  pill: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: '99px',
    fontSize: '10px', fontWeight: '700',
    background: { verde: '#d4edda', amber: '#fff3cd', azul: '#cce5ff', rojo: '#f8d7da', gris: '#e2e3e5' }[color] || '#e2e3e5',
    color:      { verde: '#155724', amber: '#856404', azul: '#004085', rojo: '#721c24', gris: '#383d41' }[color] || '#333',
  }),
};

const ESTADO_IND = {
  Pendiente:  { color:'azul',  label:'🕐 Programada' },
  Notificada: { color:'amber', label:'🔔 Aviso enviado' },
  Cumplida:   { color:'verde', label:'✔ Cumplida' },
  Vencida:    { color:'rojo',  label:'⚠ Vencida sin cumplir' },
  Cancelada:  { color:'gris',  label:'✕ Cancelada' },
};

function fmtFechaHora(s) {
  if (!s) return '—';
  try { return new Date(String(s).replace(' ', 'T')).toLocaleString('es-SV', { dateStyle:'short', timeStyle:'short' }); }
  catch (_) { return s; }
}

// Devuelve el valor por defecto para <input type="datetime-local">: ahora + 30 min, redondeado a 5 min.
function defaultDateTimeLocal() {
  const d = new Date(Date.now() + 30 * 60000);
  d.setMinutes(Math.ceil(d.getMinutes() / 5) * 5, 0, 0);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// Convierte "YYYY-MM-DDTHH:mm" (hora local del input) a "YYYY-MM-DD HH:mm:00" para SQLite.
function localInputASql(valor) {
  if (!valor) return null;
  return valor.replace('T', ' ') + ':00';
}

// Props:
//  admision_id  (requerido) — id de la admisión activa
//  medico_id    (opcional)  — médico tratante de esa admisión, para prellenar
//  usuario      (requerido) — usuario logueado (quien registra la acción)
export default function IndicacionesEnfermeriaPanel({ admision_id, medico_id, usuario }) {
  const [indicaciones, setIndicaciones] = useState([]);
  const [cargando,     setCargando]     = useState(true);
  const [mostrarForm,  setMostrarForm]  = useState(false);

  // Enfermería solo puede registrar esta indicación como orden verbal/
  // telefónica del médico — nunca crearla "a nombre propio" ni cancelarla.
  const rolesEnfermeria = ['enfermeria', 'jefa_enfermeria', 'supervisora_turno'];
  const esEnfermeria = rolesEnfermeria.includes(usuario?.rol);

  // ── Formulario nueva indicación
  const [descripcion,  setDescripcion]  = useState('');
  const [tipoProg,      setTipoProg]    = useState('unica'); // 'unica' | 'intervalo'
  const [fechaHora,     setFechaHora]   = useState(defaultDateTimeLocal());
  const [frecuencia,    setFrecuencia]  = useState('8');
  const [horaFin,       setHoraFin]     = useState('');
  const [minutosAviso,  setMinutosAviso]= useState('15');
  const [medicoAutoriza,setMedicoAutoriza] = useState('');
  const [guardando,     setGuardando]   = useState(false);
  const [error,         setError]       = useState('');
  const { confirmar, ModalConfirmarUI } = useConfirmar();

  const cargarIndicaciones = useCallback(async () => {
    if (!admision_id) { setCargando(false); return; }
    setCargando(true);
    try {
      const r = await api.indicacionesEnf.listar({ admision_id });
      if (r?.ok) setIndicaciones(r.data);
    } catch(e) { console.warn('[indicaciones_enf] Error cargando:', e.message); }
    setCargando(false);
  }, [admision_id]);

  useEffect(() => { cargarIndicaciones(); }, [cargarIndicaciones]);

  const limpiarForm = () => {
    setDescripcion(''); setTipoProg('unica'); setFechaHora(defaultDateTimeLocal());
    setFrecuencia('8'); setHoraFin(''); setMinutosAviso('15'); setMedicoAutoriza(''); setError('');
  };

  const guardarIndicacion = async () => {
    setError('');
    const erroresValidacion = [];

    if (!admision_id) erroresValidacion.push('No hay admisión activa para este paciente.');
    if (!descripcion.trim()) erroresValidacion.push('Describe la indicación a cumplir.');
    if (!fechaHora) erroresValidacion.push('Indica la fecha y hora de la indicación.');
    if (tipoProg === 'intervalo' && (!frecuencia || Number(frecuencia) <= 0)) {
      erroresValidacion.push('Indica cada cuántas horas se repite.');
    }
    if (esEnfermeria && !medicoAutoriza.trim()) {
      erroresValidacion.push('Indica el nombre del médico que autorizó esta indicación verbal/telefónica.');
    }

    if (erroresValidacion.length > 0) {
      setError(erroresValidacion.length === 1
        ? erroresValidacion[0]
        : `Hay ${erroresValidacion.length} campos por corregir:\n` + erroresValidacion.map(e => `• ${e}`).join('\n'));
      return;
    }

    setGuardando(true);
    try {
      const r = await api.indicacionesEnf.crear({
        admision_id,
        medico_id: medico_id || null,
        descripcion: descripcion.trim(),
        tipo_programacion: tipoProg,
        proxima_ejecucion: localInputASql(fechaHora),
        frecuencia_horas: tipoProg === 'intervalo' ? Number(frecuencia) : null,
        hora_fin: tipoProg === 'intervalo' ? localInputASql(horaFin) : null,
        minutos_aviso: Number(minutosAviso) || 15,
        usuario_id: usuario.id,
        usuario_rol: usuario.rol,
        origen: esEnfermeria ? 'verbal_enfermeria' : 'medico',
        medico_autoriza_nombre: esEnfermeria ? medicoAutoriza.trim() : null,
      });
      if (r?.ok) {
        limpiarForm();
        setMostrarForm(false);
        cargarIndicaciones();
      } else {
        setError(r?.error || 'No se pudo guardar la indicación.');
      }
    } catch(e) { setError(e.message); }
    setGuardando(false);
  };

  const cancelarIndicacion = async (ind) => {
    if (!(await confirmar({
      texto: `¿Cancelar la indicación "${ind.descripcion}"?`,
      tipo: 'advertencia',
      textoConfirmar: 'Cancelar indicación',
    }))) return;
    const r = await api.indicacionesEnf.cancelar({
      id: ind.id, motivo: 'Cancelada por el médico', usuario_id: usuario.id, usuario_rol: usuario.rol,
    });
    if (r?.ok) cargarIndicaciones();
    else alert(r?.error || 'No se pudo cancelar.');
  };

  if (!admision_id) {
    return (
      <div style={S.card}>
        <div style={S.titulo}><span>🔔 Indicaciones para Enfermería</span></div>
        <div style={{ color:'#adb5bd', fontSize:'13px', padding:'12px 0', textAlign:'center' }}>
          Este paciente no tiene una admisión activa. Solo se pueden programar indicaciones a pacientes hospitalizados.
        </div>
      </div>
    );
  }

  return (
    <div style={S.card}>
      {ModalConfirmarUI}
      <div style={S.titulo}>
        <span>🔔 Indicaciones para Enfermería ({indicaciones.length})</span>
        <button style={S.btnVerde} onClick={() => setMostrarForm(v => !v)}>
          {mostrarForm ? 'Cerrar' : '+ Nueva indicación'}
        </button>
      </div>

      {/* ── Formulario nueva indicación ── */}
      {mostrarForm && (
        <div style={{ background:'#f8fafd', border:'1px solid #dde3ea', borderRadius:'8px', padding:'14px', marginBottom:'16px' }}>
          {esEnfermeria && (
            <div style={{ background:'#fff3cd', border:'1px solid #ffe08a', borderRadius:'7px',
              padding:'10px 12px', marginBottom:'12px', fontSize:'12px', color:'#856404' }}>
              ⚠ Como enfermería, esta indicación solo puede registrarse como <strong>orden verbal o telefónica</strong> dada
              por un médico. Quedará marcada como tal y deberás indicar qué médico la autorizó.
            </div>
          )}

          <div style={S.campo}>
            <label style={S.label}>Indicación / acción a cumplir *</label>
            <input style={S.input} placeholder="Ej: Control de signos vitales, cambio de vía periférica, administrar antibiótico IV..."
              value={descripcion} onChange={e => setDescripcion(e.target.value)} />
          </div>

          {esEnfermeria && (
            <div style={S.campo}>
              <label style={S.label}>Médico que autorizó (verbal / telefónico) *</label>
              <input style={S.input} placeholder="Ej: Dr. Juan Pérez"
                value={medicoAutoriza} onChange={e => setMedicoAutoriza(e.target.value)} />
            </div>
          )}

          <div style={S.fila2}>
            <div style={S.campo}>
              <label style={S.label}>Repetición</label>
              <select style={S.input} value={tipoProg} onChange={e => setTipoProg(e.target.value)}>
                <option value="unica">Única vez</option>
                <option value="intervalo">Recurrente (cada N horas)</option>
              </select>
            </div>
            <div style={S.campo}>
              <label style={S.label}>{tipoProg === 'intervalo' ? 'Primera ejecución *' : 'Fecha y hora *'}</label>
              <input style={S.input} type="datetime-local" value={fechaHora} onChange={e => setFechaHora(e.target.value)} />
            </div>
          </div>

          {tipoProg === 'intervalo' && (
            <div style={S.fila2}>
              <div style={S.campo}>
                <label style={S.label}>Frecuencia (cada cuántas horas) *</label>
                <input style={S.input} type="number" min="1" step="1" placeholder="Ej: 8"
                  value={frecuencia} onChange={e => setFrecuencia(e.target.value)} />
              </div>
              <div style={S.campo}>
                <label style={S.label}>Repetir hasta (opcional)</label>
                <input style={S.input} type="datetime-local" value={horaFin} onChange={e => setHoraFin(e.target.value)} />
              </div>
            </div>
          )}

          <div style={S.campo}>
            <label style={S.label}>Avisar con cuántos minutos de anticipación</label>
            <input style={{ ...S.input, maxWidth:'140px' }} type="number" min="1" step="1"
              value={minutosAviso} onChange={e => setMinutosAviso(e.target.value)} />
            <div style={{ fontSize:'10px', color:'#94a3b8', marginTop:'4px' }}>
              Enfermería recibirá un recordatorio (campana + notificación del sistema) antes de la hora programada.
              Si pasan 30 min sin marcarse como cumplida, se marca automáticamente como "Vencida" y se alerta con prioridad alta.
            </div>
          </div>

          {error && (
            <div style={{ color:'#dc3545', fontSize:'12px', fontWeight:600, marginTop:'6px', whiteSpace:'pre-line' }}>⚠ {error}</div>
          )}

          <button style={{ ...S.btnPrimario, marginTop:'10px', opacity: guardando ? 0.6 : 1 }}
            onClick={guardarIndicacion} disabled={guardando}>
            {guardando ? 'Guardando...' : '✔ Programar indicación'}
          </button>
        </div>
      )}

      {/* ── Lista de indicaciones ── */}
      {cargando ? (
        <div style={{ textAlign:'center', padding:'20px', color:'#6c757d' }}>Cargando...</div>
      ) : indicaciones.length === 0 ? (
        <div style={{ color:'#adb5bd', fontSize:'13px', padding:'12px 0', textAlign:'center' }}>
          No hay indicaciones programadas para esta admisión.
        </div>
      ) : (
        <table style={S.tabla}>
          <thead>
            <tr>
              {['Programada para','Indicación','Repetición','Estado','Cumplida por',''].map(h => (
                <th key={h} style={S.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {indicaciones.map(ind => {
              const est = ESTADO_IND[ind.estado] || { color:'gris', label:ind.estado };
              return (
                <tr key={ind.id}>
                  <td style={{ ...S.td, fontSize:'12px', whiteSpace:'nowrap' }}>
                    {fmtFechaHora(ind.proxima_ejecucion)}
                  </td>
                  <td style={S.td}>
                    {ind.descripcion}
                    {ind.origen === 'verbal_enfermeria' && (
                      <div style={{ fontSize:'10px', color:'#856404', marginTop:'2px', fontWeight:600 }}>
                        📞 Orden verbal — autorizó: {ind.medico_autoriza_nombre || '—'}
                      </div>
                    )}
                  </td>
                  <td style={{ ...S.td, fontSize:'11px', color:'#6c757d' }}>
                    {ind.tipo_programacion === 'intervalo'
                      ? `Cada ${ind.frecuencia_horas}h${ind.hora_fin ? ' hasta ' + fmtFechaHora(ind.hora_fin) : ''}`
                      : 'Única vez'}
                  </td>
                  <td style={S.td}><span style={S.pill(est.color)}>{est.label}</span></td>
                  <td style={{ ...S.td, fontSize:'11px' }}>
                    {ind.estado === 'Cumplida' ? (
                      <div>
                        <div style={{ color:'#155724', fontWeight:600 }}>{ind.cumplida_por_nombre}</div>
                        <div style={{ color:'#6c757d' }}>{fmtFechaHora(ind.cumplida_en)}</div>
                      </div>
                    ) : '—'}
                  </td>
                  <td style={S.td}>
                    {!esEnfermeria && ['Pendiente','Notificada','Vencida'].includes(ind.estado) && (
                      <button style={{ ...S.btnSecundario, fontSize:'11px', padding:'4px 10px', color:'#dc3545' }}
                        onClick={() => cancelarIndicacion(ind)}>
                        Cancelar
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}
